import type { SignupField, SignupFieldKind } from "@ternary/contracts"
import { Button, Checkbox, Dialog, Field, IconButton, Menu, Select, Textarea } from "@ternary/ui"
import { type ReactElement, type RefObject, useId, useLayoutEffect, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { fieldKeyFromLabel } from "../product-draft.js"
import { useMedia } from "../use-wide.js"
import { NavIcon } from "./nav-icons.js"

/** The most fields a signup form can have (`signupFieldsSchema`). */
export const MAX_SIGNUP_FIELDS = 12

/** From this width a row shows Up, Down and Delete beside Edit; below it they are in a menu. */
const WIDE_ROWS = "(min-width: 48rem)"

// In the order the Type menu lists them.
const KIND_LABELS: Record<SignupFieldKind, string> = {
  text: "Short text",
  select: "Single choice",
  multiselect: "Multiple choice",
  checkbox: "Checkbox",
  date: "Date",
  phone: "Phone number",
  country: "Country",
}

// The contract keeps these keys for the type of the same name.
const TYPED_KEYS = ["phone", "country"] as const

function isChoice(kind: SignupFieldKind) {
  return kind === "select" || kind === "multiselect"
}

function fieldSummary(field: SignupField) {
  return [
    KIND_LABELS[field.kind],
    field.options ? `${field.options.length} options` : null,
    field.required ? "Required" : "Optional",
  ]
    .filter(Boolean)
    .join(" · ")
}

type FormErrors = { label?: string; options?: string }

function withoutError(errors: FormErrors, key: keyof FormErrors): FormErrors {
  const { [key]: _removed, ...rest } = errors
  return rest
}

function optionLines(text: string) {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
}

function checkField(label: string, kind: SignupFieldKind, options: string[]): FormErrors {
  const errors: FormErrors = {}
  const name = label.trim()
  if (!name) errors.label = "Enter a label."
  else if (name.length > 80) errors.label = "Use 80 characters or fewer."
  if (!isChoice(kind)) return errors
  if (options.length === 0) errors.options = "Add at least one option."
  else if (options.length > 30) errors.options = "Use 30 options or fewer."
  else if (options.some((option) => option.length > 60)) {
    errors.options = "Keep each option to 60 characters or fewer."
  } else if (new Set(options).size !== options.length) {
    errors.options = "Each option must be different."
  }
  return errors
}

/**
 * The dialog's fields. They mount with the dialog, so every open starts from `field`. They are not
 * a <form>: the editor sits inside the wizard's form, and forms do not nest. The dialog's Save
 * button calls `saveRef`, and Enter in the label saves too.
 */
function SignupFieldForm({
  field,
  saveRef,
  taken,
  onSave,
}: {
  field: SignupField | null
  saveRef: RefObject<(() => void) | null>
  taken: string[]
  onSave(field: SignupField): void
}) {
  const [label, setLabel] = useState(field?.label ?? "")
  const [kind, setKind] = useState<SignupFieldKind>(field?.kind ?? "text")
  const [required, setRequired] = useState(field?.required ?? false)
  const [options, setOptions] = useState(field?.options?.join("\n") ?? "")
  const [errors, setErrors] = useState<FormErrors>({})
  const formRef = useRef<HTMLDivElement>(null)
  const labelRef = useRef<HTMLTextAreaElement>(null)
  const typedKey = TYPED_KEYS.find((key) => key === field?.key)

  function submit() {
    const list = optionLines(options)
    const found = checkField(label, kind, list)
    // Rendered now, so the first invalid field can take focus.
    flushSync(() => setErrors(found))
    if (Object.keys(found).length > 0) {
      formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
      return
    }
    // A new field never takes the phone or country key unless it has that type.
    const blocked = [...taken, ...TYPED_KEYS.filter((key) => key !== kind)]
    onSave({
      key: field?.key ?? fieldKeyFromLabel(label, blocked),
      kind,
      label: label.trim(),
      required,
      step: 1,
      ...(isChoice(kind) ? { options: list } : {}),
    })
  }

  // The latest submit, for the dialog's Save button.
  useLayoutEffect(() => {
    saveRef.current = submit
  })

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: a key guard for the controls inside, not a control itself.
    <div
      className="admin-stack"
      onKeyDown={(event) => {
        // Enter would submit the wizard's form around the editor, losing the field. It is kept
        // for a new line in the options, pressing a button and following a link; in the label it
        // saves.
        const target = event.target as HTMLElement
        if (event.key !== "Enter" || event.nativeEvent.isComposing) return
        if (target === labelRef.current) {
          event.preventDefault()
          submit()
          return
        }
        if (target instanceof HTMLTextAreaElement || target instanceof HTMLButtonElement) return
        if (target.closest("a")) return
        event.preventDefault()
      }}
      ref={formRef}
    >
      {/* One line that grows to three, so a long label shows whole. Enter saves; it never breaks.
          It counts what Save keeps, the label without spaces at either end. */}
      <Field
        aside={<span className="admin-count">{`${label.trim().length}/80`}</span>}
        error={errors.label}
        label="Label"
        required
      >
        <Textarea
          className="admin-field-label-input"
          data-autofocus
          onChange={(event) => {
            // A pasted line break becomes a space, as a one-line input would take it.
            setLabel(event.currentTarget.value.replace(/\s*[\r\n]+\s*/g, " "))
            setErrors((current) => withoutError(current, "label"))
          }}
          ref={labelRef}
          rows={1}
          value={label}
        />
      </Field>
      <Field
        hint={
          typedKey
            ? `This field always asks for a ${KIND_LABELS[typedKey].toLowerCase()}.`
            : undefined
        }
        label="Type"
      >
        <Select
          disabled={Boolean(typedKey)}
          onChange={(event) => setKind(event.currentTarget.value as SignupFieldKind)}
          value={kind}
        >
          {Object.entries(KIND_LABELS).map(([value, name]) => (
            <option key={value} value={value}>
              {name}
            </option>
          ))}
        </Select>
      </Field>
      {isChoice(kind) ? (
        <Field error={errors.options} hint="One per line, up to 30." label="Options" required>
          {/* Never soft-wrapped, so a long option never reads as two. */}
          <Textarea
            className="admin-field-options"
            onChange={(event) => {
              setOptions(event.currentTarget.value)
              setErrors((current) => withoutError(current, "options"))
            }}
            rows={4}
            value={options}
            wrap="off"
          />
        </Field>
      ) : null}
      <Checkbox
        checked={required}
        label="Required"
        onChange={(event) => setRequired(event.currentTarget.checked)}
      />
    </div>
  )
}

/**
 * Adds or edits one signup field: its label (1–80 characters), type, whether it is required, and
 * for a choice its options, one per line (1–30, each up to 60 characters). A new field's key comes
 * from its label, unique among `taken`; an edited field keeps its key, so the answers already
 * given stay with it.
 */
export function SignupFieldDialog({
  field,
  open,
  taken,
  onSave,
  onCancel,
}: {
  field: SignupField | null
  open: boolean
  taken: string[]
  onSave(field: SignupField): void
  onCancel(): void
}): ReactElement {
  const saveRef = useRef<(() => void) | null>(null)
  return (
    <Dialog
      footer={
        <>
          <Button onClick={onCancel} variant="secondary">
            Cancel
          </Button>
          <Button onClick={() => saveRef.current?.()}>Save field</Button>
        </>
      }
      onClose={onCancel}
      open={open}
      title={field ? "Edit field" : "Add field"}
    >
      <SignupFieldForm field={field} onSave={onSave} saveRef={saveRef} taken={taken} />
    </Dialog>
  )
}

type RowAction = "edit" | "up" | "down" | "delete"

/**
 * The extra questions of a signup form, after the name and email: up to 12 compact rows (label,
 * type and whether it is required), each with Edit, Up, Down and Delete; on a phone, Edit and a
 * menu of the other three. Add field and Edit open `SignupFieldDialog`. After a move or a delete,
 * focus stays on the row's controls.
 */
export function SignupFieldsEditor({
  value,
  onChange,
  error,
}: {
  value: SignupField[]
  onChange(value: SignupField[]): void
  error?: string | undefined
}): ReactElement {
  const id = useId()
  // The field the dialog edits: an index, "new", or null while it is closed.
  const [editing, setEditing] = useState<number | "new" | null>(null)
  // A new object each time, so the same request focuses again.
  const [focus, setFocus] = useState<{ key: string; actions: RowAction[] } | null>(null)
  const listRef = useRef<HTMLOListElement>(null)
  const addRef = useRef<HTMLButtonElement>(null)
  // Where there is no matchMedia (jsdom), the wide row.
  const wide = useMedia(WIDE_ROWS, true)
  const full = value.length >= MAX_SIGNUP_FIELDS
  const current = typeof editing === "number" ? (value[editing] ?? null) : null

  useLayoutEffect(() => {
    if (!focus) return
    const row = listRef.current?.querySelector(`[data-field-key="${focus.key}"]`)
    const target = focus.actions
      .map((action) => row?.querySelector<HTMLButtonElement>(`[data-action="${action}"]`))
      .find((button) => button && !button.disabled)
    // A phone row has no Up, Down or Delete button: its menu holds them.
    const menu = row?.querySelector<HTMLButtonElement>('[aria-haspopup="menu"]')
    ;(target ?? menu ?? addRef.current)?.focus()
  }, [focus])

  function move(index: number, by: -1 | 1) {
    const field = value[index]
    const other = value[index + by]
    if (!field || !other) return
    const next = [...value]
    next[index] = other
    next[index + by] = field
    onChange(next)
    // A field moved to the end has that button disabled, so focus goes to the other one.
    setFocus({ actions: by < 0 ? ["up", "down"] : ["down", "up"], key: field.key })
  }

  function remove(index: number) {
    const next = value.filter((_, at) => at !== index)
    onChange(next)
    const neighbour = next[index] ?? next[index - 1]
    setFocus({ actions: ["delete"], key: neighbour?.key ?? "" })
  }

  function save(field: SignupField) {
    if (editing === "new") {
      onChange([...value, field])
      // Add field may now be disabled, so focus goes to the new row.
      setFocus({ actions: ["edit"], key: field.key })
    } else if (typeof editing === "number") {
      onChange(value.map((each, at) => (at === editing ? field : each)))
    }
    setEditing(null)
  }

  return (
    <div className="admin-fields">
      <div className="admin-fields-toolbar">
        <p className="admin-note" id={`${id}-count`}>
          {full
            ? `${MAX_SIGNUP_FIELDS} of ${MAX_SIGNUP_FIELDS} fields, the most a signup form can have`
            : `${value.length} of ${MAX_SIGNUP_FIELDS} fields`}
        </p>
        <Button
          disabled={full}
          onClick={() => setEditing("new")}
          ref={addRef}
          size="sm"
          variant="secondary"
        >
          <NavIcon name="plus" />
          Add field
        </Button>
      </div>
      {value.length > 0 ? (
        <ol
          aria-describedby={error ? `${id}-error` : undefined}
          aria-label="Signup fields"
          className="admin-field-list"
          ref={listRef}
          // biome-ignore lint/a11y/noRedundantRoles: Safari drops the list role of a list styled with list-style: none.
          role="list"
        >
          {value.map((field, index) => (
            <li className="admin-field-row" data-field-key={field.key} key={field.key}>
              <span className="admin-field-text">
                <span className="admin-field-label" title={field.label}>
                  {field.label}
                  {field.required ? (
                    <span aria-hidden="true" className="tny-required">
                      {" "}
                      *
                    </span>
                  ) : null}
                </span>
                <span className="admin-field-meta">{fieldSummary(field)}</span>
              </span>
              <span className="admin-field-actions">
                <IconButton
                  data-action="edit"
                  icon={<NavIcon name="edit" />}
                  label={`Edit ${field.label}`}
                  onClick={() => setEditing(index)}
                  size="sm"
                />
                {wide ? (
                  <>
                    <IconButton
                      data-action="up"
                      disabled={index === 0}
                      icon={<NavIcon name="up" />}
                      label={`Move ${field.label} up`}
                      onClick={() => move(index, -1)}
                      size="sm"
                    />
                    <IconButton
                      data-action="down"
                      disabled={index === value.length - 1}
                      icon={<NavIcon name="down" />}
                      label={`Move ${field.label} down`}
                      onClick={() => move(index, 1)}
                      size="sm"
                    />
                    <IconButton
                      className="admin-field-delete"
                      data-action="delete"
                      icon={<NavIcon name="trash" />}
                      label={`Delete ${field.label}`}
                      onClick={() => remove(index)}
                      size="sm"
                    />
                  </>
                ) : (
                  // A phone row has room for two 44px targets: Edit, and the rest in a menu.
                  <Menu
                    icon={<NavIcon name="more" />}
                    items={[
                      { disabled: index === 0, label: "Move up", onSelect: () => move(index, -1) },
                      {
                        disabled: index === value.length - 1,
                        label: "Move down",
                        onSelect: () => move(index, 1),
                      },
                      { danger: true, label: "Delete", onSelect: () => remove(index) },
                    ]}
                    label={`Actions for ${field.label}`}
                  />
                )}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="admin-note">No extra fields: members give only their name and email.</p>
      )}
      {error ? (
        <p className="tny-error" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : null}
      <SignupFieldDialog
        field={current}
        onCancel={() => setEditing(null)}
        onSave={save}
        open={editing !== null}
        taken={value.filter((field) => field !== current).map((field) => field.key)}
      />
    </div>
  )
}
